import { useCallback, useState } from "react";

import { datadogRum } from "@datadog/browser-rum";

import { useAppLanguageStore } from "app/stores/useAppLanguageStore";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { translatedInboundUnitFromSearchResult } from "shared/models/productSearchUnit/serializer";
import {
  SearchUnitsByEanQuery,
  useSearchUnitsByEanLazyQuery,
} from "shared/queries/productSearchUnit/productSearchUnit.generated";

import { SelectedProduct } from "./inboundStore/types";

export function useAddItemToDroppingList() {
  const appLanguage = useAppLanguageStore((state) => state.appLanguage);
  const { showToastUI } = useCustomToast();
  const [isScannedProductLoading, setIsScannedProductLoading] = useState(false);

  const handleError = useCallback(
    (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      datadogRum.addError("Error while fetching units by ean", { errorMessage });
      showToastUI({
        id: "get_units_by_ean_error_toast_id",
        title: "get_units_by_ean_error_toast_id",
        description: "get_units_by_ean_error_toast_description",
      });
    },
    [showToastUI],
  );
  const [searchUnitsByEan] = useSearchUnitsByEanLazyQuery({
    fetchPolicy: "no-cache",
  });

  const fetchProductByEan = async (ean: string): Promise<SelectedProduct | null> => {
    if (isScannedProductLoading) return null;
    setIsScannedProductLoading(true);

    try {
      const { data } = await searchUnitsByEan({
        variables: {
          searchUnitsByEanInput: { ean },
          isAlwaysInbound: true,
        },
      });

      const units = data?.searchUnitsByEan?.units || [];
      if (!data || units.length === 0) return null;

      const uniqueProductSkus = new Set<string>();
      const filteredUnits = units.reduce(
        (acc, unit) => {
          if (!uniqueProductSkus.has(unit.productSku)) {
            uniqueProductSkus.add(unit.productSku);
            acc.push(unit);
          }
          return acc;
        },
        [] as SearchUnitsByEanQuery["searchUnitsByEan"]["units"],
      );

      const translatedUnits = translatedInboundUnitFromSearchResult(filteredUnits, appLanguage);

      const scannedProduct = translatedUnits[0];
      return {
        sku: scannedProduct.productSku,
        quantity: scannedProduct.quantity,
        handlingUnitSize: scannedProduct.handlingUnitSize ?? 1,
      };
    } catch (err) {
      handleError(err);
      return null;
    } finally {
      setIsScannedProductLoading(false);
    }
  };

  return { fetchProductByEan };
}
